<template>
  <div>
    <div
      class="evaluationoptions"
      v-if="visibleEvaluationOptions && visibleEvaluationOptions.length > 0"
    >
      <div class="title">
        <gl-sub-text>評価表示</gl-sub-text>
      </div>
      <div class="content">
        <md-radio
          v-for="e in visibleEvaluationOptions"
          :key="e"
          v-model="_evaluationOption"
          :value="e"
        >
          {{ $store.state.labels.evaluationOption[e] }}
        </md-radio>
      </div>
    </div>
    <div v-if="visibleCustomerSegments && visibleCustomerSegments.length > 0">
      <div class="title">
        <gl-sub-text>顧客セグメント</gl-sub-text>
      </div>
      <div class="content">
        <md-radio
          v-for="e in visibleCustomerSegments"
          :key="e"
          v-model="_customerSegment"
          :value="e"
        >
          {{ $store.state.labels.customerSegment[e] }}
        </md-radio>
      </div>
    </div>
    <div
      class="evaluations"
      v-if="visibleEvaluations && visibleEvaluations.length > 0"
    >
      <div class="title">
        <gl-sub-text>評価項目</gl-sub-text>
      </div>
      <!-- 実数系 -->
      <div class="content" v-if="rawFigureEvaluations.length > 0">
        <md-radio
          v-for="e in rawFigureEvaluations"
          :key="e"
          v-model="_evaluation"
          :value="e"
        >
          {{ $store.state.labels.evaluations[e] }}
        </md-radio>
      </div>
      <!-- 算出系 -->
      <div class="content" v-if="computedFigureEvaluations.length > 0">
        <md-radio
          v-for="e in computedFigureEvaluations"
          :key="e"
          v-model="_evaluation"
          :value="e"
        >
          {{ $store.state.labels.evaluations[e] }}
        </md-radio>
      </div>
      <!-- 非会員含系 -->
      <div class="content" v-if="totalFigureEvaluations.length > 0">
        <md-radio
          v-for="e in totalFigureEvaluations"
          :key="e"
          v-model="_evaluation"
          :value="e"
        >
          {{ $store.state.labels.evaluations[e] }}
        </md-radio>
      </div>
    </div>
  </div>
</template>

<script>
import { intersection } from 'lodash'
import ErrorReport from '@/mixins/ErrorReport'
import EVALUATION_OPTIONS from '@/const/evaluationoptions'
import CUSTOMER_SEGMENTS from '@/const/customersegments'
import EVALUATIONS from '@/const/evaluations'

// サポート項目に含まれるか否か（構成比、実数）
function isValidEvaluationOptions(values) {
  for (const v of values) {
    if (!EVALUATION_OPTIONS.includes(v)) {
      return false
    }
  }
  return true
}

// 全ての要素がサポートする評価項目に含まれるか否か
function isValidCustomerSegmentValues(values) {
  for (const v of values) {
    if (!CUSTOMER_SEGMENTS.includes(v)) {
      return false
    }
  }
  return true
}

// 全ての要素がサポートする評価項目に含まれるか否か
function isValidEvaluationValues(values) {
  for (const v of values) {
    if (!EVALUATIONS.includes(v)) {
      return false
    }
  }
  return true
}

/**
 * オプション選択コンポーネント
 *
 * 基本形として定義していて、以下の選択を行うオプションです。
 * * 顧客セグメントの選択（どれか一つ）
 * * 評価項目の選択（任意複数）
 *
 * 指定可能な顧客セグメント、評価項目は絞ることができます（propsを参照してください）。
 *
 * Usage）
 *
 * ```html
 * <filter-option
 *   v-bind.sync="queryFilter.option"
 *   :visible-evaluations="[
 *     'customer_count',
 *     'qty',
 *     'amount',
 *     'receipt_count',
 *   ]"
 * ```
 *
 * ```javascript
 * // 略
 * data() {
 *   return {
 *     queryFilter: {
 *       customerSegment: 'rank',
 *       evaluations: ['customer_count', 'amount']
 *     }
 *   }
 * }
 * // 略
 * ```
 */
export default {
  mixins: [ErrorReport],
  props: {
    /**
     * 構成比、実数の表示選択
     *
     * 指定がない場合、指定可能な全項目を表示します
     * 空配列を指定された場合、選択欄は表示されません。
     */
    visibleEvaluationOptions: {
      type: Array,
      default: () => [],
      validator: isValidEvaluationOptions,
    },
    /**
     * 選択肢として表示する顧客セグメント
     *
     * 指定がない場合、指定可能な全ての顧客セグメントを表示します。
     * 空配列を指定された場合、顧客セグメントの選択欄は表示されません。
     */
    visibleCustomerSegments: {
      type: Array,
      default: () => [...CUSTOMER_SEGMENTS],
      validator: isValidCustomerSegmentValues,
    },
    /**
     * 選択肢として表示する評価項目
     *
     * 指定がない場合、指定可能な全ての評価項目を表示します。
     * 空配列を指定された場合、評価項目の選択欄は表示されません。
     */
    visibleEvaluations: {
      type: Array,
      default: () => [...EVALUATIONS],
      validator: isValidEvaluationValues,
    },
    evaluationOption: {
      type: String,
      validator: value => EVALUATION_OPTIONS.includes(value),
    },
    /**
     * 選択されている顧客セグメント
     */
    customerSegment: {
      type: String,
      validator: value => CUSTOMER_SEGMENTS.includes(value),
    },
    /**
     * 選択されている評価項目
     */
    evaluation: {
      type: String,
      default: () => 'amount',
      validator: value => EVALUATIONS.includes(value),
    },
  },
  computed: {
    /**
     * 実数系の評価項目
     */
    rawFigureEvaluations() {
      return intersection(
        ['amount', 'qty', 'receipt_count', 'customer_count'],
        this.visibleEvaluations,
      )
    },
    /**
     * 計算系の評価項目
     */
    computedFigureEvaluations() {
      return intersection(
        [
          'amount_per_qty',
          'qty_per_receipt',
          'amount_per_receipt',
          'receipt_per_customer',
          'qty_per_customer',
          'amount_per_customer',
        ],
        this.visibleEvaluations,
      )
    },
    /**
     * 非会員系（トータル）の評価項目
     */
    totalFigureEvaluations() {
      return intersection(
        ['total_amount', 'total_qty', 'total_receipt_count'],
        this.visibleEvaluations,
      )
    },
    /**
     * ローカルの構成比、実数選択
     *
     * MdRadioのv-model用に用意
     */
    _evaluationOption: {
      get() {
        return this.evaluationOption
      },
      set(newVal) {
        this.$emit('update:evaluationOption', newVal)
      },
    },
    /**
     * ローカルの顧客セグメント値
     *
     * MdRadioのv-model用に用意
     */
    _customerSegment: {
      get() {
        return this.customerSegment
      },
      set(newVal) {
        this.$emit('update:customerSegment', newVal)
      },
    },
    /**
     * ローカルの評価項目
     *
     * MdRadioのv-model用に用意
     */
    _evaluation: {
      get() {
        return this.evaluation
      },
      set(newVal) {
        this.$emit('update:evaluation', newVal)
      },
    },
    /**
     * 入力エラー
     */
    errors() {
      const errors = []

      if (
        this.visibleCustomerSegments &&
        this.visibleCustomerSegments.length > 0 &&
        !this.customerSegment
      ) {
        errors.push('顧客セグメントを選択してください')
      }

      if (!this.evaluation || this.evaluation.length === 0) {
        errors.push('評価項目を選択してください')
      }

      return errors
    },
  },
}
</script>

<style lang="scss" scoped>
.title {
  margin-top: var(--spacing-medium);
}

.evaluations {
  .content:not(:last-child) {
    border-bottom: 1px solid var(--color-border);
  }
}
</style>
